import React, {
  useEffect, useMemo, useState,
} from 'react';
import classnames from 'classnames';
import Cookies from 'universal-cookie';

import { joinNames } from '../../../utils/common';
import { patchResidence } from '../../../utils/DrupalEntityTypes/Residence/ResidenceHelpers';
import {
  useConfigStore, useAppStore, useAuthStore, useMapDataStore, useMapInteractionStore,
} from '../../../stores';
import BasicDropdown from '../../BasicDropdown/BasicDropdown';
import { ResidenceStatusWidgetPropTypes, ResidenceStatusWidgetDefaultProps } from './ResidenceStatusWidgetPropTypes';

import './ResidenceStatusWidget.scss';

const cookies = new Cookies();

const ResidenceStatusWidget = ( {
  residenceUuid,
  residenceTitle,
  residenceSvgId,
  residents,
  statusUuid,
  residenceStatus,
  setFetchUnitData,
  setDetailDialogData,
} ) => {
  const [ statusPhrase, setStatusPhrase ] = useState( '' );
  const [ changeStatusIsOpen, setChangeStatusIsOpen ] = useState( false );
  const [ allowStatusChange, setAllowStatusChange ] = useState( false );
  const [ buttonText, setButtonText ] = useState( 'Change Status' );
  const [ selectedStatusObject, setSelectedStatusObject ] = useState( {} );
  // Bumped on open/cancel to remount BasicDropdown so its internal selection
  // resets to the residence's current status (it has no controlled value prop).
  const [ dropdownRemountKey, setDropdownRemountKey ] = useState( 0 );

  // Zustand stores
  const featureFlags = useConfigStore((state) => state.featureFlags);
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn);
  const personalizationActive = useAppStore((state) => state.personalizationActive);
  const residenceStatusOptions = useMapDataStore((state) => state.residenceStatusOptions);

  const { platformShowOccupants, platformShowOccupancy } = featureFlags || {};

  // Derive from config so they stay in sync on refresh (no local state / effect timing).
  const showOccupancy = Boolean( platformShowOccupancy );
  const showOccupants = Boolean( platformShowOccupancy && platformShowOccupants );

  // Map interaction store
  const setChangedUnits = useMapInteractionStore((state) => state.setChangedUnits);

  // Shape the status terms for BasicDropdown (needs .text and .isDefault) while
  // preserving the fields the Save flow reads (.id, .name, fieldConsiderUnoccupied).
  // Memoized so the reference is stable across renders — BasicDropdown resets its
  // selection whenever the options reference changes, which would otherwise revert
  // a staged pick on every re-render.
  const statusDropdownOptions = useMemo(
    () => ( residenceStatusOptions || [] ).map( ( option ) => ( {
      ...option,
      text: option.name,
      isDefault: option.name === residenceStatus,
    } ) ),
    [ residenceStatusOptions, residenceStatus ],
  );

  // Allow status change if a sales person is logged in.
  useEffect( () => {
    if ( isLoggedIn ) {
      setAllowStatusChange( true );
    }
  }, [ isLoggedIn ] );

  // Stage the residence's current status as the initial selection, and keep it in
  // sync when the residence data refreshes after a save.
  useEffect( () => {
    if ( !residenceStatusOptions || !Array.isArray( residenceStatusOptions ) ) {
      return;
    }

    const initialOption = residenceStatusOptions.find(
      ( option ) => option.name === residenceStatus,
    );
    if ( initialOption ) {
      setSelectedStatusObject( initialOption );
    }
  }, [ residenceStatusOptions, residenceStatus ] );

  // Set status phrase - "Status: [status] for [residents]" phrase.
  useEffect( () => {
    let rawStatusPhrase = showOccupancy ? residenceStatus : null;
    if ( residents?.length > 0 && showOccupants ) {
      const residentNamesString = joinNames(
        residents[0]?.firstName,
        residents[0]?.lastName,
        residents[1]?.firstName,
        residents[1]?.lastName,
        null,
        '&',
      );

      switch ( residenceStatus ) {
        case 'Sold':
          rawStatusPhrase = `Sold to ${residentNamesString}`;
          break;
        case 'Reserved':
          rawStatusPhrase = `Reserved for ${residentNamesString}`;
          break;
        case 'Occupied':
          rawStatusPhrase = `Occupied by ${residentNamesString}`;
          break;
        default:
          rawStatusPhrase = `${residenceStatus} for ${residentNamesString}`;
          break;
      }
    }
    setStatusPhrase( rawStatusPhrase );
  }, [
    residenceStatus,
    residents,
    showOccupancy,
    showOccupants,
  ] );

  // Set button text.
  useEffect( () => {
    if ( personalizationActive && ( !residents || residents.length === 0 ) ) {
      setButtonText( 'Make it Yours' );
    }
  }, [ personalizationActive ] );

  // Create status change message.
  const makeSuccessMessage = async ( updatedResidence, updatedResidenceStatusTerm ) => {
    const updatedResidenceStatusName = updatedResidenceStatusTerm?.name;
    let message = '';
    if ( !personalizationActive ) {
      message = `Residence ${residenceTitle}'s status has been updated to ${updatedResidenceStatusName}.`;
    } else {
      // We could generate this based on component data, but let's use the
      // response to confirm Drupal really did update correctly.
      const updatedResidenceTitle = updatedResidence?.data?.attributes?.title;
      const updatedResidenceStatusConsideredUnoccupied = updatedResidenceStatusTerm
        ?.fieldConsiderUnoccupied;

      if ( updatedResidenceStatusConsideredUnoccupied ) {
        message = `Residence ${updatedResidenceTitle}'s status has been updated to ${updatedResidenceStatusName}.`;
      } else {
        // Get names from cookie.
        // We're keeping properties drupal php style.
        // eslint-disable-next-line camelcase
        const { first_name, person2_first_name } = cookies.get( 'resident' );
        const combinedFirstNames = joinNames( first_name, null, person2_first_name, null, null, '&' );
        const namePhrase = combinedFirstNames ? ` ${combinedFirstNames}` : '';
        const lcStatus = updatedResidenceStatusName.toLowerCase();

        message = `Congratulations${namePhrase}! `;
        switch ( updatedResidenceStatusName ) {
          case 'Sold':
          case 'Occupied':
            message += `Residence ${updatedResidenceTitle} is now yours.`;
            break;
          // Reserved, On Hold, etc.
          default:
            message += `Residence ${updatedResidenceTitle} is now ${lcStatus} for you.`;
            break;
        }
      }
    }
    return message;
  };

  const handleSaveClick = async () => {
    // Close status widget.
    setChangeStatusIsOpen( false );

    // Potential new residents.
    const cookieData = cookies.get( 'resident' );

    const activePersonalizationResidentUuids = [];
    // Check if we are adding or removing residents.
    if ( cookieData && !selectedStatusObject?.fieldConsiderUnoccupied ) {
      activePersonalizationResidentUuids.push( cookieData.drupal_uuid );
      if ( cookieData.person2_drupal_uuid ) {
        activePersonalizationResidentUuids.push( cookieData.person2_drupal_uuid );
      }
    }

    // Sort uuids just so we can compare them.
    activePersonalizationResidentUuids.sort();

    const residentUuids = [];
    residents.forEach(( resident ) => {
      residentUuids.push(resident.uuid);
    });
    residentUuids.sort();

    // Don't do anything if the status didn't change
    // AND the residents didn't change
    if ( selectedStatusObject.id === statusUuid
      && ( activePersonalizationResidentUuids === residentUuids )
    ) {
      return;
    }

    // Send residence update to Drupal.
    let response = null;

    // If personalization is active.
    if ( cookieData ) {
      // Update residence. This will also update the residents.
      // For now, we are not going to bother removing them from other
      // residences. This will kick current residents out if they aren't the
      // active personalization resident.
      response = await patchResidence(
        residenceUuid,
        activePersonalizationResidentUuids,
        selectedStatusObject.id,
      );
    } else {
      // If personalization not active.
      response = await patchResidence(
        residenceUuid,
        null,
        selectedStatusObject.id,
      );
    }

    // Alert user that residence(s) has been updated or if there's an error.
    let errorMessage = '';
    let updatedResidence = null;
    let updatedResidenceStatusTerm = null;
    if ( response ) {
      // Error
      if ( response?.errors ) {
        response.errors.forEach( ( responseError ) => {
          errorMessage += `${responseError.detail}. `;
        } );
      } else {
        updatedResidence = response;

        // Check that the returned status matches what we sent.
        const updatedResidenceStatusUuid = updatedResidence
          ?.data?.relationships?.field_status?.data?.id;
        updatedResidenceStatusTerm = residenceStatusOptions.find(
          ( option ) => option.id === updatedResidenceStatusUuid,
        );

        if ( updatedResidenceStatusUuid !== selectedStatusObject.id ) {
          errorMessage += 'Residence status did not update correctly. ';
        }
        if ( !updatedResidenceStatusTerm ) {
          errorMessage += 'Residence does not have an assigned status. ';
        }

        // Check that the returned residents matches what we sent.
        const updatedResidenceStatusConsideredUnoccupied = updatedResidenceStatusTerm
          ?.fieldConsiderUnoccupied;
        const rawUpdatedResidenceResidentUuids = updatedResidence
          ?.data?.relationships?.field_residents?.data;
        const comparableUpdatedResidenceResidentUuids = [];
        rawUpdatedResidenceResidentUuids?.forEach( ( resident ) => {
          comparableUpdatedResidenceResidentUuids.push( resident.id );
        });

        // Sort array so we can compare it to the active personalization uuids.
        comparableUpdatedResidenceResidentUuids.sort();

        // If the unit isn't now considered unoccupied and there are active
        // personalization uuids
        if ( !updatedResidenceStatusConsideredUnoccupied
          && activePersonalizationResidentUuids.length > 0 ) {
          if ( comparableUpdatedResidenceResidentUuids === activePersonalizationResidentUuids ) {
            errorMessage += 'Residence occupants did not update correctly. ';
          }
        } else if ( updatedResidenceStatusConsideredUnoccupied
          && comparableUpdatedResidenceResidentUuids?.length > 0 ) {
          // The updated unit is considered unoccupied so there shouldn't be any uuids.
          errorMessage += 'Residence occupants did not update correctly. ';
        }
      }
    } else {
      // Nothing returned - that's an issue too.
      errorMessage += 'Something went wrong. ';
    }

    // There are errors so show error message.
    if ( errorMessage !== '' ) {
      errorMessage += 'Please reload the page and try again.';
      setDetailDialogData( {
        status: 'error',
        message: errorMessage,
      } );
    } else {
      // Success!
      const message = await makeSuccessMessage( updatedResidence, updatedResidenceStatusTerm );
      setDetailDialogData( {
        status: 'success',
        message,
      } );

      // Reload the residence data.
      setFetchUnitData( true );

      // Let the map and filters know about the status change.
      // Ensure field_svg_id is present in the response (JSON:API PATCH may not include it)
      const changedUnitData = {
        ...updatedResidence,
        data: {
          ...updatedResidence?.data,
          attributes: {
            ...updatedResidence?.data?.attributes,
            field_svg_id: updatedResidence?.data?.attributes?.field_svg_id || residenceSvgId,
          },
        },
      };
      setChangedUnits( [ changedUnitData ] );
    }
  };

  const handleCancelClick = () => {
    // Discard the staged pick: reset back to the residence's current status and
    // remount BasicDropdown so its internal selection reverts to match.
    const initialOption = residenceStatusOptions.find(
      ( option ) => option.name === residenceStatus,
    );
    setSelectedStatusObject( initialOption );
    setDropdownRemountKey( ( key ) => key + 1 );
    setChangeStatusIsOpen( false );
  };

  return showOccupancy && (
    <div
      className={
        classnames( 'residenceStatusWidget', {
          'residenceStatusWidget--changeStatusIsOpen': changeStatusIsOpen,
        } )
      }
    >
      <div className="residenceStatusWidget__statusContainer">

        <div className="detailTable__itemLabel detailTable__itemLabel--list">
          Status:
        </div>

        { !allowStatusChange && (
          <div className="detailTable__itemData">
            { statusPhrase }
          </div>
        )}

        { allowStatusChange && (
          <>
            <div
              className={
                classnames( 'residenceStatusWidget__status', {
                  'residenceStatusWidget__status--active': !changeStatusIsOpen,
                } )
              }
            >

              <div className="detailTable__itemData residenceStatusWidget__statusPhrase">
                { statusPhrase }
              </div>

              <div className="residenceStatusWidget__reserveBtnWrapper">
                <button
                  className="residenceStatusWidget__reserveBtn"
                  type="button"
                  onClick={() => {
                    // Remount BasicDropdown so it opens showing the current status.
                    setDropdownRemountKey( ( key ) => key + 1 );
                    setChangeStatusIsOpen( true );
                  }}
                >
                  { buttonText }
                </button>
              </div>

            </div>

            <div
              className={
                classnames( 'residenceStatusWidget__changeStatus', {
                  'residenceStatusWidget__changeStatus--active': changeStatusIsOpen,
                } )
              }
            >

              <BasicDropdown
                key={dropdownRemountKey}
                className="residenceStatusWidget__dropdown"
                options={statusDropdownOptions}
                handleOptionOnClick={( option ) => setSelectedStatusObject( option )}
              />

              <div className="residenceStatusWidget__actionBtnsWrapper">
                <button
                  type="button"
                  className="residenceStatusWidget__save"
                  onClick={() => {
                    handleSaveClick();
                  }}
                >
                  Save
                </button>

                <button
                  type="button"
                  className="residenceStatusWidget__cancel"
                  onClick={() => {
                    handleCancelClick();
                  }}
                >
                  Cancel
                </button>
              </div>

            </div>
          </>
        )}

      </div>
    </div>
  );
};

ResidenceStatusWidget.propTypes = ResidenceStatusWidgetPropTypes;
ResidenceStatusWidget.defaultProps = ResidenceStatusWidgetDefaultProps;

export default ResidenceStatusWidget;
